import { useEffect, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import type { ExportFieldOption } from "./ComponentTableExportDialog"
import { buildNameToFieldMeta, formatKeyForDisplay, getComparableKey } from "./itemFieldComparableKey"

export type ComponentTableCompareDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  rows: Record<string, unknown>[]
  exportableFieldsFromMeta: ExportFieldOption[]
  meta: unknown
  currentMeta: unknown
}

function shortUidLabel(uid: string): string {
  const t = uid.trim()
  if (t.length <= 10) return t
  return `${t.slice(0, 6)}…${t.slice(-4)}`
}

export function ComponentTableCompareDialog({
  open,
  onOpenChange,
  rows,
  exportableFieldsFromMeta,
  meta,
  currentMeta,
}: ComponentTableCompareDialogProps) {
  const [showAllFields, setShowAllFields] = useState(false)

  useEffect(() => {
    if (open) setShowAllFields(false)
  }, [open])

  const nameToFieldMeta = useMemo(() => {
    return { ...buildNameToFieldMeta(meta), ...buildNameToFieldMeta(currentMeta) }
  }, [meta, currentMeta])

  const compareRows = useMemo(() => {
    const seen = new Set<string>()
    const out: Record<string, unknown>[] = []
    for (const r of rows) {
      const uid = typeof (r as { uid?: unknown }).uid === "string" ? (r as { uid: string }).uid : ""
      if (!uid || seen.has(uid)) continue
      seen.add(uid)
      out.push(r as Record<string, unknown>)
    }
    return out
  }, [rows])

  const fieldRows = useMemo(() => {
    return exportableFieldsFromMeta.map((f) => {
      const keys = compareRows.map((row) => getComparableKey(row, f.name, nameToFieldMeta))
      const first = keys[0]
      const differs =
        keys.length < 2 || first === undefined || keys.some((k) => k !== first)
      return {
        name: f.name,
        label: f.label,
        keys,
        differs,
      }
    })
  }, [compareRows, exportableFieldsFromMeta, nameToFieldMeta])

  const visibleFieldRows = useMemo(
    () => (showAllFields ? fieldRows : fieldRows.filter((r) => r.differs)),
    [fieldRows, showAllFields]
  )

  const allMatch = fieldRows.length > 0 && fieldRows.every((r) => !r.differs)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[min(90vh,800px)] min-h-0 w-full flex-col gap-4 overflow-hidden p-6",
          "sm:max-w-[min(96vw,1200px)]"
        )}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Compare selected items</DialogTitle>
          <DialogDescription>
            Values use the same rules as duplicate detection (including creator, timestamps, and relations). Rows
            with the same item id appear once. Highlighted rows differ across the selection.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
          {compareRows.length < 2 ? (
            <p className="text-sm text-muted-foreground">
              Select at least two different items in the table, then open Compare again.
            </p>
          ) : (
            <>
              <label className="flex cursor-pointer items-start gap-2 shrink-0">
                <Checkbox
                  checked={showAllFields}
                  onCheckedChange={(checked) => setShowAllFields(checked === true)}
                  className="mt-0.5"
                />
                <span className="text-sm leading-snug text-foreground">
                  Show all fields{" "}
                  <span className="text-muted-foreground">
                    (off: only fields that differ between selected items)
                  </span>
                </span>
              </label>

              {!showAllFields && allMatch ? (
                <p className="text-sm text-muted-foreground shrink-0">
                  All compared fields match across the selected items.
                </p>
              ) : null}

              <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border">
                <table className="w-full min-w-[520px] border-collapse text-sm">
                  <thead className="sticky top-0 z-[1] border-b border-border bg-muted/80 backdrop-blur-sm">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left font-medium text-foreground whitespace-nowrap"
                      >
                        Field
                      </th>
                      {compareRows.map((row, colIdx) => {
                        const uid = String((row as { uid?: string }).uid ?? "")
                        return (
                          <th
                            key={uid || `col-${colIdx}`}
                            scope="col"
                            className="px-3 py-2 text-left font-medium text-foreground align-bottom min-w-[10rem]"
                          >
                            <span className="block font-mono text-xs text-muted-foreground" title={uid}>
                              {uid ? shortUidLabel(uid) : "(no uid)"}
                            </span>
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFieldRows.map((fr) => (
                      <tr
                        key={fr.name}
                        className={cn(
                          "border-b border-border/80",
                          fr.differs ? "bg-amber-500/10 dark:bg-amber-500/15" : undefined
                        )}
                      >
                        <th
                          scope="row"
                          className="px-3 py-2 text-left font-normal align-top text-foreground max-w-[14rem]"
                        >
                          <span className="break-words">{fr.label}</span>
                        </th>
                        {fr.keys.map((k, i) => (
                          <td
                            key={`${fr.name}-${i}`}
                            className="px-3 py-2 align-top text-foreground break-words max-w-[24rem]"
                          >
                            {formatKeyForDisplay(fr.name, k)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
